import { Link } from "react-router-dom";
import { Heart, ShoppingBag, Star } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { formatINR, discountPct } from "@/lib/api";

export default function ProductCard({ product, testIdPrefix = "product" }) {
  const { addToCart, toggleWishlist, inWishlist } = useApp();
  const disc = discountPct(product.price, product.mrp);
  const active = inWishlist(product.id);
  const outOfStock = (product.stock || 0) <= 0;

  return (
    <div data-testid={`${testIdPrefix}-card-${product.slug}`} className="sa-card group relative flex flex-col overflow-hidden">
      <Link to={`/product/${product.slug}`} className="relative block aspect-square overflow-hidden bg-black">
        {product.images?.[0] ? (
          <img
            src={product.images[0]}
            alt={product.name}
            className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/20 font-display text-xs uppercase">No Image</div>
        )}
        {disc > 0 && (
          <span className="absolute top-3 left-3 bg-[#E63946] text-white overline px-2 py-1">-{disc}%</span>
        )}
        {product.tags?.includes("new") && (
          <span className="absolute top-3 right-3 bg-white text-black overline px-2 py-1">NEW</span>
        )}
        {outOfStock && (
          <span className="absolute bottom-3 left-3 bg-black/80 text-white overline px-2 py-1 border border-white/20">Currently Unavailable</span>
        )}
      </Link>

      <button
        onClick={(e) => { e.preventDefault(); toggleWishlist(product); }}
        className={`absolute top-3 right-3 md:top-auto md:bottom-[86px] md:right-3 w-8 h-8 flex items-center justify-center bg-black/60 backdrop-blur-sm border border-white/10 ${active ? "text-[#E63946]" : "text-white/80"} hover:text-[#E63946]`}
        aria-label="Wishlist"
        data-testid={`wishlist-toggle-${product.slug}`}
      >
        <Heart size={16} fill={active ? "currentColor" : "none"} />
      </button>

      <div className="p-4 flex-1 flex flex-col">
        <Link to={`/product/${product.slug}`} className="block">
          <h3 className="text-sm leading-snug line-clamp-2 min-h-[2.5rem] mb-2 group-hover:text-white/90">{product.name}</h3>
        </Link>
        <div className="flex items-center gap-1 mb-2">
          {[0,1,2,3,4].map((i) => (
            <Star key={i} size={12} fill={i < Math.round(product.rating || 4) ? "#E63946" : "transparent"} stroke="#E63946" />
          ))}
          <span className="text-[10px] text-white/50 ml-1">({product.review_count || 0})</span>
        </div>
        <div className="flex items-baseline gap-2 mt-auto">
          <span className="font-display text-lg text-white">{formatINR(product.price)}</span>
          {product.mrp > product.price && <span className="text-xs text-white/40 line-through">{formatINR(product.mrp)}</span>}
        </div>
        {!outOfStock ? (
          <button
            onClick={() => addToCart(product, 1)}
            className="mt-3 sa-btn sa-btn-dark w-full text-xs py-3"
            data-testid={`quick-add-${product.slug}`}
          >
            <ShoppingBag size={14} /> QUICK ADD
          </button>
        ) : (
          <Link to={`/product/${product.slug}`} className="mt-3 sa-btn sa-btn-outline w-full text-xs py-3" data-testid={`notify-me-${product.slug}`}>
            NOTIFY ME
          </Link>
        )}
      </div>
    </div>
  );
}
